import { mpEventInfo } from '@/constant'
import type { Student, StudentWorksheet, Worksheet } from '@/dto'
import type { SchoolGrade } from '@/dto/Student/SchoolGrade'
import type { DefaultResponse, Pagination } from '@/types'
import type { CustomRequestConfigMeta } from '@/utils/axios/maxios'
import maxios from '@/utils/axios/maxios'
/***
 * @link https://freewheelin.postman.co/collections/5542627-31bd5fa5-3069-4f49-8c6d-97a718edb97e?version=latest&workspace=ef6c4fe1-201a-4de6-8b0c-2a7de439d3ff#f06edccf-74ca-4dba-aa89-05ecb1d75838
 ***/

// 학생-학습지 API
const studentWorksheetApi = {
  // n개 학습지를 n명에게 출제
  postStudentWorksheets: (studentIdList: Student['id'][], worksheetIdList: Worksheet['id'][]) => {
    return maxios.post<DefaultResponse<res.PostStudentWorksheet>>(
      '/student-worksheet',
      {
        studentIdList,
        worksheetIdList,
      },
      {
        params: { mp_event_name: mpEventInfo.학습지출제 },
      },
    )
  },
  // (경고!!!) 학생에게 출제된 학습지 삭제하기
  deleteStudentWorksheets(studentWorksheetIdList: StudentWorksheet['id'][]) {
    return maxios({
      url: '/student-worksheet',
      method: 'DELETE',
      data: studentWorksheetIdList,
      params: { mp_event_name: mpEventInfo.학습지출제취소 },
    })
  },
  // 학생에게 출제된 특정 학습지 가져오기
  getStudentWorksheet: (StudentWorksheetId: StudentWorksheet['id']) => {
    return maxios.get<DefaultResponse<res.StudentWorksheet>>(
      `/student-worksheet/assign/${StudentWorksheetId}`,
      {
        params: { mp_event_name: mpEventInfo.출제된학습지상세조회 },
      },
    )
  },
  // 학생에게 출제된 학습지 리스트 가져오기
  getStudentWorksheets: (studentId: Student['id'], pageQueryString: string) => {
    return maxios.get<Pagination<res.StudentWorksheet[]>>(
      `/student-worksheet/${studentId}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.특정학생학습지조회 },
      },
    )
  },
  // 학습지의 문제/채점기록 가져오기
  getStudentWorksheetProblems: (
    studentWorksheetId: StudentWorksheet['id'],
    pageQueryString: string,
    meta?: { meta: CustomRequestConfigMeta },
  ) => {
    return maxios.get<Pagination<res.StudentWorksheetProblem[]>>(
      `/student-worksheet/assign/${studentWorksheetId}/problem${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.출제된학습지문제조회 },
        ...meta,
      },
    )
  },
  // 학습지 채점
  patchStudentWorksheetScoring(
    studentWorksheetId: StudentWorksheet['id'],
    data: req.ScoringResult[],
  ) {
    return maxios.patch<DefaultResponse<res.ScoringResult>>(
      `/student-worksheet/assign/${studentWorksheetId}/scoring`,
      data,
      {
        params: { mp_event_name: mpEventInfo.학습지문제당채점 },
      },
    )
  },
  // 학습지, 학생-학습지 정보 수정
  patchStudentWorksheet(
    studentWorksheetId: StudentWorksheet['id'],
    dateTime: { [key in 'assignDatetime' | 'scoreDatetime']?: string },
  ) {
    return maxios.patch(`/student-worksheet/assign/${studentWorksheetId}`, dateTime, {
      params: { mp_event_name: mpEventInfo.학습지출제일채점일수정 },
    })
  },
  // 학년별로 그학습지 리스트를 가져온다
  getGradeStudentWorksheets: (
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<res.GroupStudentWorksheet[]>>(
      `/student-worksheet/grade/${schoolType}/${grade}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.학년별출제된학습지조회 },
      },
    )
  },
  // 학년별로 그룹 출제된 학습지의 채점을 가져온다
  getGradeStudentWorksheetProblems: (
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    worksheetId: StudentWorksheet['worksheet']['id'],
    pageQueryString: string,
    meta?: { meta: CustomRequestConfigMeta },
  ) => {
    return maxios.get<Pagination<res.GroupStudentWorksheetProblem[]>>(
      `/student-worksheet/grade/${schoolType}/${grade}/${worksheetId}/scoring${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.학년별출제된학습지채점조회 },
        ...meta,
      },
    )
  },
  // 반별로 그룹 학습지 리스트를 가져온다
  getClassStudentWorksheets: (lessonClassId: string, pageQueryString: string) => {
    return maxios.get<Pagination<res.GroupStudentWorksheet[]>>(
      `/student-worksheet/lesson-class/${lessonClassId}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.반별출제된학습지조회 },
      },
    )
  },
  // 반별로 그룹 출제된 학습지의 채점을 가져온다
  getClassStudentWorksheetProblems: (
    lessonClassId: string,
    worksheetId: StudentWorksheet['worksheet']['id'],
    pageQueryString: string,
    meta?: { meta: CustomRequestConfigMeta },
  ) => {
    return maxios.get<Pagination<res.GroupStudentWorksheetProblem[]>>(
      `/student-worksheet/lesson-class/${lessonClassId}/${worksheetId}/scoring${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.반별출제된학습지채점조회 },
        ...meta,
      },
    )
  },

  // 가상 채점 학습지 조회
  getStudentWorksheetVirtualScoringProblems: (studentWorksheetIds: StudentWorksheet['id'][]) => {
    return maxios.get<DefaultResponse<res.StudentWorksheetVirtualScoringProblem[]>>(
      `/student-worksheet/virtual-scoring`,
      {
        params: { mp_event_name: mpEventInfo.학년별출제된학습지채점조회, studentWorksheetIds },
      },
    )
  },
}

export default studentWorksheetApi
